import React from "react";

// Cinematic SVG map of Australia with pulsing hubs (Melbourne · Perth) and
// animated dashed coverage lines fanning out to the rest of the country.
// All coordinates are tuned to a stylised 600x450 outline.
export default function AnimatedAusMap({ className = "" }) {
  // Stylised coordinates inside the 600x450 viewBox
  const HUBS = [
    { id: "mel", x: 415, y: 340, label: "Melbourne", colour: "#6B3FA0" }, // primary purple
    { id: "per", x: 95,  y: 285, label: "Perth",     colour: "#4FA56E" }, // green
  ];
  const REACH = [
    { id: "syd", x: 460, y: 245, label: "Sydney" },
    { id: "bne", x: 470, y: 175, label: "Brisbane" },
    { id: "adl", x: 320, y: 305, label: "Adelaide" },
    { id: "cbr", x: 445, y: 290, label: "Canberra" },
    { id: "hba", x: 425, y: 415, label: "Hobart" },
    { id: "drw", x: 240, y: 110, label: "Darwin" },
  ];

  return (
    <svg
      viewBox="0 0 600 450"
      className={className}
      role="img"
      aria-label="Alpha Community Care national coverage map — operational hubs in Melbourne and Perth, intake coverage Australia-wide"
    >
      <defs>
        <linearGradient id="ausFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#1f103f" />
          <stop offset="50%"  stopColor="#3E2478" />
          <stop offset="100%" stopColor="#6B3FA0" />
        </linearGradient>
        <radialGradient id="pulsePurple" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="rgba(134,89,199,0.85)" />
          <stop offset="100%" stopColor="rgba(134,89,199,0)" />
        </radialGradient>
        <radialGradient id="pulseGreen" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="rgba(79,165,110,0.85)" />
          <stop offset="100%" stopColor="rgba(79,165,110,0)" />
        </radialGradient>
        <linearGradient id="lineFlow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.05)" />
          <stop offset="50%"  stopColor="rgba(255,255,255,0.55)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background fill */}
      <rect width="600" height="450" fill="url(#ausFill)" />

      {/* Subtle grid */}
      <g opacity="0.10" stroke="#FFFFFF" strokeWidth="0.5">
        {Array.from({ length: 12 }, (_, i) => (
          <line key={`h${i}`} x1="0" x2="600" y1={i * 38} y2={i * 38} />
        ))}
        {Array.from({ length: 16 }, (_, i) => (
          <line key={`v${i}`} x1={i * 38} x2={i * 38} y1="0" y2="450" />
        ))}
      </g>

      {/* Australia outline (stylised) */}
      <path
        d="M80,225 C58,250 50,300 90,325 C125,348 140,358 200,365 C275,375 360,378 425,360 C475,348 500,328 515,308 C535,278 540,245 510,222 C475,205 432,213 400,200 C365,185 335,175 280,185 C228,195 175,178 138,195 C112,205 92,212 80,225 Z"
        fill="rgba(255,255,255,0.04)"
        stroke="rgba(255,255,255,0.45)"
        strokeWidth="1.4"
        strokeDasharray="4 3"
      />
      {/* Tasmania */}
      <ellipse cx="425" cy="408" rx="22" ry="11" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeDasharray="3 3" />

      {/* Coverage lines from hubs to other capitals */}
      {HUBS.flatMap((hub) =>
        REACH.map((r, i) => {
          // Curve control point — pull toward centre for a graceful arc.
          const mx = (hub.x + r.x) / 2;
          const my = Math.min(hub.y, r.y) - 18 - (i * 4);
          const d = `M${hub.x},${hub.y} Q${mx},${my} ${r.x},${r.y}`;
          const len = 600;
          return (
            <g key={`${hub.id}-${r.id}`}>
              <path d={d} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.8" />
              <path d={d} fill="none" stroke="url(#lineFlow)" strokeWidth="1.6" strokeLinecap="round" strokeDasharray={`${len * 0.18} ${len * 0.82}`}>
                <animate attributeName="stroke-dashoffset" from={`${len}`} to={`0`} dur={`${4 + (i * 0.6) + (hub.id === "per" ? 1 : 0)}s`} repeatCount="indefinite" />
              </path>
            </g>
          );
        })
      )}

      {/* Reach pins (smaller) */}
      {REACH.map((r) => (
        <g key={r.id}>
          <circle cx={r.x} cy={r.y} r="4" fill="#FFFFFF" opacity="0.9" />
          <circle cx={r.x} cy={r.y} r="2" fill="#6B3FA0" />
          <text x={r.x + 8} y={r.y - 6} fontSize="9" fontFamily="Inter, system-ui, sans-serif" fontWeight="600" fill="rgba(255,255,255,0.65)">{r.label}</text>
        </g>
      ))}

      {/* Hub pulses */}
      {HUBS.map((h, i) => (
        <g key={h.id} filter="url(#glow)">
          <circle cx={h.x} cy={h.y} r="42" fill={h.id === "mel" ? "url(#pulsePurple)" : "url(#pulseGreen)"}>
            <animate attributeName="r" values="22;58;22" dur="3.4s" begin={`${i * 1.7}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.85;0;0.85" dur="3.4s" begin={`${i * 1.7}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={h.x} cy={h.y} r="10" fill="#FFFFFF" />
          <circle cx={h.x} cy={h.y} r="5" fill={h.colour} />
          <text x={h.x + 12} y={h.y + 4} fontSize="13" fontFamily="Fraunces, Georgia, serif" fontWeight="600" fill="#FFFFFF">{h.label}</text>
          <text x={h.x + 12} y={h.y + 18} fontSize="9" fontFamily="Inter, system-ui, sans-serif" fontWeight="700" letterSpacing="0.18em" fill="rgba(255,255,255,0.6)">HUB</text>
        </g>
      ))}

      {/* Floating label cards */}
      <g>
        <rect x="20" y="20" width="180" height="46" rx="10" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.25)" />
        <text x="32" y="42" fontSize="10" fontFamily="Inter, system-ui, sans-serif" fontWeight="700" letterSpacing="0.2em" fill="rgba(255,255,255,0.7)">VIC · WA HUBS</text>
        <text x="32" y="58" fontSize="14" fontFamily="Fraunces, Georgia, serif" fontWeight="600" fill="#FFFFFF">Operational on the ground</text>
      </g>
      <g>
        <rect x="395" y="20" width="185" height="46" rx="10" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.25)" />
        <text x="408" y="42" fontSize="10" fontFamily="Inter, system-ui, sans-serif" fontWeight="700" letterSpacing="0.2em" fill="rgba(255,255,255,0.7)">NATIONAL INTAKE</text>
        <text x="408" y="58" fontSize="14" fontFamily="Fraunces, Georgia, serif" fontWeight="600" fill="#FFFFFF">8 states &amp; territories</text>
      </g>
    </svg>
  );
}
